import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { takeUntil, Observable, take, map, filter, withLatestFrom } from 'rxjs';
import { MenuController } from '@ionic/angular';
import { Authentication, HomeState, Store as StoreModel } from '@core/models';
import { ComponentEnum, LanguajeEnum, OrderTypeEnum } from '@core/enums';
import { CartService, I18nService, SwitchService } from '@core/services';
import { IAbstractPresenter, ILanguageObject, IUserLocation } from '@core/interfaces';
import {
	selectAuth,
	selectClientLocation,
	selectLanguaje,
	selectOrderType,
	selectStore,
} from '@store/selectors';
import { CORE_CHANGE_LANGUAJE } from '@store/actions';
import { IHeaderComponent, IHeaderPresenter } from './interfaces';
import { CartProcessorService } from '@/features/shoppingcart/domain';
import { homeProcessor } from '@/features/home/domain';

@Injectable()
export class HeaderPresenter extends IAbstractPresenter<IHeaderComponent> implements IHeaderPresenter {
	auth$: Observable<Authentication | null>;
	translations: ILanguageObject;
	store$: Observable<StoreModel | null>;
	clientLocation$: Observable<IUserLocation | null>;
	totalCartCount$: Observable<number>;
	orderType$: Observable<string | null>;
	languageEnum$: Observable<LanguajeEnum>;
	name: string | null = null;
	icon: string = 'user';
	
	constructor(
		private store: Store<HomeState>,
		private menuCtrl: MenuController,
		private i18nService: I18nService,
		private switchService: SwitchService,
		private cartService: CartService,
		private cartProcessor: CartProcessorService,
	) {
		super();
		this.auth$ = this.store.select(selectAuth);
		this.store$ = this.store.select(selectStore);
		this.clientLocation$ = this.store.select(selectClientLocation);
		this.orderType$ = this.store.select(selectOrderType);
		this.languageEnum$ = this.store.select(selectLanguaje);
		this.totalCartCount$ = this.cartService.getStoreForStore().pipe(
			map(items => this.cartProcessor.getTotalQuantity(items))
		);
		this.translations = this.i18nService.getTranslations(ComponentEnum.Home, LanguajeEnum.en);
	}

	dispatchActions(): void {}

	suscribeObservers(): void {
		this.languageEnum$.pipe(takeUntil(this.destroy$)).subscribe(lang => {
			this.view.translations = this.i18nService.getTranslations(ComponentEnum.Home, lang);
		});

		this.auth$.pipe(takeUntil(this.destroy$)).subscribe(auth => {
				const { icon } = homeProcessor.process({
					auth,
					products: [],
					isProductsLoading: false,
				});

				this.view.name = auth?.nombre ?? null;
				this.view.icon = icon;
			});
	}

	toggleLanguage(): void {
		this.languageEnum$.pipe(take(1)).subscribe(currentLang => {
			const newLang = currentLang === LanguajeEnum.es ? LanguajeEnum.en : LanguajeEnum.es;
			this.store.dispatch(CORE_CHANGE_LANGUAJE({ languaje: newLang }));
		});
	}

	async openDeliveryModal(): Promise<void> {
		this.switchService.openDeliveryModal();
	}

	onOpenCartMenu(): void {
		this.menuCtrl.open('cartMenu');
	}

	openMainMenu(): void {
		this.menuCtrl.open();
	}

	getStoreAddress(): Observable<string | null> {
		return this.store$.pipe(
			filter((store) => !!store),
			take(1),
			map(store => {
				return store?.ubicacion?.direccion ?? null;
			})
		);
	}	

	getAddress(): Observable<string | null> {
		return this.store$
			.pipe(
				withLatestFrom(
					this.orderType$,
					this.clientLocation$,
				),
				filter(([store, orderType]) => !!store && !!orderType),
				take(1),
				map(([store, orderType, clientLocation]) => {
					return homeProcessor.process({
						auth: null,
						products: [],
						isProductsLoading: false,
						orderType: orderType as OrderTypeEnum,
						userLocation: clientLocation,
						storeAddress: store?.ubicacion?.direccion,
					}).address;
				}
			)
		)
	}
}
